import { ordersBasketApiConnector } from 'apiConnector/orders-basket';
import { tabsApiConnector } from 'apiConnector/tabs/tabs';
import { ofType } from 'redux-observable';
import { of, zip } from 'rxjs';
import { catchError, filter, map, mergeMap, take } from 'rxjs/operators';
import {
  ordersBasketFormMetadataPersisted,
  ordersBasketFormMetadataPersistenceFailed,
  ordersBasketFormMetadataRemovalFailed,
  ordersBasketFormMetadataRemoved,
} from 'state/orders-basket/actions';
import {
  ordersBasketPersistedFormFetchedThunk,
  ordersBasketPersistedFormFetchFailedThunk,
} from 'state/orders-basket/thunks';
import type { EpicWithThunk } from 'state/state.common';
import {
  ordersBasketTabContentFetchFailed,
  ordersBasketTabContentPersisted,
  ordersBasketTabContentPersistenceFailed,
} from 'state/workspaces/ordersBasket/tabContent/tabContent.actions';
import { ordersBasketTabContentFetchedThunk } from 'state/workspaces/ordersBasket/tabContent/tabContent.thunks';
import { workspaceTabsFetchFailed, workspaceTabsFetchRequested } from 'state/workspaces/workspace.actions';
import { workspaceTabsFetchedThunk } from 'state/workspaces/workspace.thunks';
import type { HttpError } from 'utils/http.old/httpClient.model';

export function fetchOrdersBasketTabsEpic(): EpicWithThunk {
  return (actions$, _state$) =>
    zip(
      actions$.pipe(ofType('USER_CURRENT_RECEIVED'), take(1)),
      actions$.pipe(
        ofType('ROUTE_CHANGED'),
        filter(({ path }) => path === '/quote-blotter'),
        take(1),
      ),
    ).pipe(map(() => workspaceTabsFetchRequested('ordersBasket')));
}

export function fetchOrdersBasketWorkspaceTabsEpic(): EpicWithThunk {
  return (actions$, _state$, { httpClient }) =>
    actions$.pipe(
      ofType('WORKSPACE_TABS_FETCH_REQUESTED'),
      filter(({ workspace }) => workspace === 'ordersBasket'),
      mergeMap(({ tabFetchRequestId }) =>
        tabsApiConnector(httpClient, 'ordersBasket')
          .getAllTabDescriptions()
          .pipe(
            map(({ tabs, activeTabId }) => {
              const actualActiveTabId: string | undefined = activeTabId ?? tabs[0]?.id ?? undefined;
              return workspaceTabsFetchedThunk(tabs, actualActiveTabId, 'ordersBasket', tabFetchRequestId);
            }),
            catchError((_: Error) => {
              return of(workspaceTabsFetchFailed('ordersBasket', tabFetchRequestId));
            }),
          ),
      ),
    );
}

export function ordersBasketTabContentPersistenceRequestedEpic(): EpicWithThunk {
  return (actions$, _state$, { oldHttpClient, frontLogger }) =>
    actions$.pipe(
      ofType('ORDERS_BASKET_TAB_CONTENT_PERSISTENCE_REQUESTED'),
      mergeMap(action => {
        return ordersBasketApiConnector(oldHttpClient)
          .SaveTabContent(action.tabId, action.tabContent)
          .pipe(
            map(() => ordersBasketTabContentPersisted(action.tabId)),
            catchError((error: Error) => {
              frontLogger.error(
                'An error occurred inside ordersBasketTabContentPersistenceRequestedEpic {@FrontError}',
                error,
              );
              return of(ordersBasketTabContentPersistenceFailed(action.tabId));
            }),
          );
      }),
    );
}

export function ordersBasketTabContentFetchRequestedEpic(): EpicWithThunk {
  return (actions$, _state$, { oldHttpClient, frontLogger }) =>
    actions$.pipe(
      ofType('ORDERS_BASKET_TAB_CONTENT_FETCH_REQUESTED'),
      mergeMap(action => {
        return ordersBasketApiConnector(oldHttpClient)
          .GetTabContent(action.tabId)
          .pipe(
            map(response => ordersBasketTabContentFetchedThunk(action.tabId, response)),
            catchError((error: Error) => {
              frontLogger.error(
                'An error occurred inside ordersBasketTabContentFetchRequestedEpic {@FrontError}',
                error,
              );
              return of(ordersBasketTabContentFetchFailed(action.tabId));
            }),
          );
      }),
    );
}

export function ordersBasketFormMetadataPersistenceRequestedEpic(): EpicWithThunk {
  return (actions$, _state$, { oldHttpClient, frontLogger }) =>
    actions$.pipe(
      ofType('ORDERS_BASKET_FORM_METADATA_PERSISTENCE_REQUESTED'),
      mergeMap(action => {
        return ordersBasketApiConnector(oldHttpClient)
          .SaveMetadata(action.formId, action.metadata)
          .pipe(
            map(() => ordersBasketFormMetadataPersisted(action.formId)),
            catchError((error: Error) => {
              frontLogger.error(
                'An error occurred inside ordersBasketFormMetadataPersistenceRequestedEpic {@FrontError}',
                error,
              );
              return of(ordersBasketFormMetadataPersistenceFailed(action.formId));
            }),
          );
      }),
    );
}

export function ordersBasketFormMetadataRemovalRequestedEpic(): EpicWithThunk {
  return (actions$, _state$, { oldHttpClient, frontLogger }) =>
    actions$.pipe(
      ofType('ORDERS_BASKET_FORM_METADATA_REMOVAL_REQUESTED'),
      mergeMap(action => {
        return ordersBasketApiConnector(oldHttpClient)
          .RemoveMetadata(action.formId)
          .pipe(
            map(() => ordersBasketFormMetadataRemoved(action.formId)),
            catchError((error: Error) => {
              frontLogger.error(
                'An error occurred inside ordersBasketFormMetadataRemovalRequestedEpic {@FrontError}',
                error,
              );
              return of(ordersBasketFormMetadataRemovalFailed(action.formId));
            }),
          );
      }),
    );
}

export function ordersBasketPersistedFormFetchRequestedEpic(): EpicWithThunk {
  return (actions$, _state$, { oldHttpClient, frontLogger }) =>
    actions$.pipe(
      ofType('ORDERS_BASKET_PERSISTED_FORM_FETCH_REQUESTED'),
      mergeMap(action => {
        return ordersBasketApiConnector(oldHttpClient)
          .FetchPersistedForm(action.formId)
          .pipe(
            map(response => ordersBasketPersistedFormFetchedThunk(action.formId, response)),
            catchError((error: HttpError) => {
              frontLogger.error(
                'An error occurred inside ordersBasketPersistedFormFetchRequestedEpic {@FrontError}',
                error,
              );
              return of(ordersBasketPersistedFormFetchFailedThunk(action.formId, error));
            }),
          );
      }),
    );
}
